import React from 'react';
import { InfoTooltip } from '../atomic/Tooltip';

interface SliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  label?: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconColor?: string;
  unit?: string;
  compact?: boolean;
  onChange: (value: number) => void;
  className?: string;
  tooltip?: string | { title: string; description: string };
  showValueLabel?: boolean;
  formatValue?: (value: number) => string;
  gradientColor?: string;
}

export function Slider({
  min,
  max,
  step,
  value,
  label,
  icon: Icon,
  iconColor,
  unit = '',
  compact = false,
  onChange,
  className = '',
  tooltip,
  showValueLabel = true,
  formatValue,
  gradientColor,
}: SliderProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };
  
  // Calculate percentage for gradient
  const percentage = ((value - min) / (max - min)) * 100;
  const displayValue = formatValue ? formatValue(value) : `${value > 0 ? '+' : ''}${value}${unit}`;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className={`flex justify-between items-center ${compact ? 'mb-1' : 'mb-2'}`}>
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1`}>
            {Icon && <Icon className={`w-3 h-3 ${iconColor || ''}`} />}
            {label}
            {tooltip && <InfoTooltip content={
              typeof tooltip === 'string' ? tooltip : tooltip.description
            } />}
          </span>
          {showValueLabel && (
            <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 dark:text-white`}>
              {displayValue}
            </span>
          )}
        </div>
      )}
      <div className="relative">
        {/* Gradient background track */}
        {gradientColor && (
          <div 
            className={`absolute inset-0 ${compact ? 'h-1' : 'h-1.5'} bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden`}
          >
            <div 
              className={`h-full ${gradientColor} transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
        
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className={`
            relative w-full ${compact ? 'h-1' : 'h-1.5'} rounded-full appearance-none cursor-pointer
            ${gradientColor ? 'bg-transparent' : 'bg-gray-200 dark:bg-gray-700'}
            [&::-webkit-slider-thumb]:appearance-none
            ${compact ? '[&::-webkit-slider-thumb]:h-3' : '[&::-webkit-slider-thumb]:h-4'}
            ${compact ? '[&::-webkit-slider-thumb]:w-3' : '[&::-webkit-slider-thumb]:w-4'}
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-white
            [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-green-600
            [&::-webkit-slider-thumb]:cursor-grab
            [&::-webkit-slider-thumb]:active:cursor-grabbing
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:transition-all
            [&::-webkit-slider-thumb]:hover:scale-110
            ${compact ? '[&::-moz-range-thumb]:h-3' : '[&::-moz-range-thumb]:h-4'}
            ${compact ? '[&::-moz-range-thumb]:w-3' : '[&::-moz-range-thumb]:w-4'}
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-white
            [&::-moz-range-thumb]:border-2
            [&::-moz-range-thumb]:border-green-600
            [&::-moz-range-thumb]:cursor-grab
            [&::-moz-range-thumb]:active:cursor-grabbing
            [&::-moz-range-thumb]:shadow-md
            [&::-moz-range-thumb]:transition-all
            [&::-moz-range-thumb]:hover:scale-110
          `}
        />
      </div>
      {!label && (
        <div className={`flex justify-between ${compact ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-gray-400 mt-1`}>
          <span>{min}{unit}</span>
          <span>{max}{unit}</span>
        </div>
      )}
    </div>
  );
}
